"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { HiX, HiZoomIn } from "react-icons/hi";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import PageHero from "@/components/sections/PageHero";
import { cn } from "@/lib/utils";
import { getGalleryPageItems } from "@/data/gallery";
import { useLanguage } from "@/lib/i18n";

export default function GalleryPage() {
  const { language } = useLanguage();
  const galleryItems = getGalleryPageItems(language);

  const [activeCategory, setActiveCategory] = useState("All");
  const [selectedImage, setSelectedImage] = useState<
    (typeof galleryItems)[0] | null
  >(null);
  const [isMobile, setIsMobile] = useState(false);

  // Get unique categories from data
  const categories = [
    "All",
    ...Array.from(new Set(galleryItems.map((item) => item.category))),
  ];

  const pageContent =
    language === "id"
      ? {
          title: "Galeri Premium KinKen",
          subtitle:
            "Sebuah visual journey melalui setiap ruang dan fasilitas KinKen Kost Elite. Temukan keindahan desain kontemporer yang dipadukan dengan sentuhan kemewahan Bali dalam setiap frame.",
          superText: "Visual Portfolio",
        }
      : {
          title: "KinKen Premium Gallery",
          subtitle:
            "A visual journey through every space and facility of KinKen Kost Elite. Discover the beauty of contemporary design combined with Balinese luxury in every frame.",
          superText: "Visual Portfolio",
        };

  const filteredImages =
    activeCategory === "All"
      ? galleryItems
      : galleryItems.filter((item) => item.category === activeCategory);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && selectedImage) {
        setSelectedImage(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedImage]);

  return (
    <main className="min-h-screen bg-white">
      <Navbar />

      <PageHero
        title={pageContent.title}
        subtitle={pageContent.subtitle}
        image="/images/gallery/gallery-hero.webp"
        superText={pageContent.superText}
      />

      <section className="py-12 md:py-20 lg:py-32 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-12">
          {/* CATEGORY TABS */}
          <div className="flex flex-wrap justify-center gap-2 md:gap-3 lg:gap-4 mb-8 md:mb-12 lg:mb-16">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={cn(
                  "px-3 py-2 md:px-4 md:py-2 lg:px-6 lg:py-3 text-[10px] md:text-xs font-bold uppercase tracking-[0.15em] md:tracking-[0.2em] transition-all duration-300 border whitespace-nowrap",
                  activeCategory === cat
                    ? "bg-brand-charcoal text-white border-brand-charcoal"
                    : "bg-white text-gray-400 border-gray-200 hover:border-brand-gold hover:text-brand-gold",
                )}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* GALLERY GRID */}
          <motion.div
            layout
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 auto-rows-[250px] sm:auto-rows-[200px] md:auto-rows-[250px] lg:auto-rows-[300px] gap-3 md:gap-4"
          >
            <AnimatePresence>
              {filteredImages.map((item) => (
                <motion.div
                  key={item.id}
                  layoutId={`gallery-${item.id}`}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.5 }}
                  className={cn(
                    "relative overflow-hidden group cursor-pointer bg-gray-100 rounded-sm w-full h-full min-h-[200px]",
                    isMobile ? "col-span-1" : item.className,
                  )}
                  onClick={() => setSelectedImage(item)}
                >
                  <Image
                    src={item.src}
                    alt={item.title}
                    fill
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                    className="object-cover transition-transform duration-700 group-hover:scale-110"
                    priority={item.id === 1}
                  />

                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center text-white p-4">
                    <HiZoomIn
                      size={isMobile ? 24 : 32}
                      className="mb-2 opacity-80"
                    />
                    <span className="font-serif text-sm md:text-lg tracking-wide text-center px-2">
                      {item.title}
                    </span>
                    <span className="text-[8px] md:text-[10px] uppercase tracking-widest mt-1 text-gray-300">
                      {item.category}
                    </span>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* LIGHTBOX MODAL */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 flex flex-col items-center justify-center p-4 md:p-6 lg:p-8 backdrop-blur-sm cursor-pointer"
            onClick={() => setSelectedImage(null)}
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 md:top-6 md:right-6 lg:top-8 lg:right-8 text-white/80 hover:text-white transition-colors z-50 p-2 bg-black/40 rounded-full hover:bg-black/60 backdrop-blur-sm"
            >
              <HiX size={isMobile ? 24 : 28} />
            </button>

            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative w-full max-w-6xl h-full flex flex-col items-center justify-center cursor-default"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative w-full h-[60vh] md:h-[70vh] lg:h-[75vh] max-h-[800px] flex items-center justify-center mb-4 md:mb-6">
                <div className="relative w-full h-full max-w-[90vw] md:max-w-[85vw] lg:max-w-[80vw]">
                  <Image
                    src={selectedImage.src}
                    alt={selectedImage.title}
                    fill
                    sizes="(max-width: 768px) 95vw, (max-width: 1024px) 90vw, 85vw"
                    className="object-contain rounded-sm"
                    priority
                    quality={90}
                  />
                </div>
              </div>

              <div className="w-full max-w-3xl mx-auto text-center px-4">
                <h3 className="font-serif text-base md:text-xl lg:text-2xl text-white mb-1 md:mb-2">
                  {selectedImage.title}
                </h3>
                <p className="text-[10px] md:text-xs uppercase tracking-wider text-gray-300">
                  {selectedImage.category}
                </p>
              </div>

              <div className="h-8 md:h-12 lg:h-16"></div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </main>
  );
}
